import React, { useEffect, useLayoutEffect, useState, useSyncExternalStore } from "react";
import { createPortal } from "react-dom";

import HttpManager from "../../../services/HttpManager";
import { DialerItem, ProspectCatalogs, ProspectRow } from "../../../services/v2/prospects.service";
import { interesesCortos } from "../../../utils/cursoCorto";
import { telefonoBonito } from "../../../utils/telefonoBonito";
import WappIcono from "../../../components/wappIcono";
import { CarteraSelect } from "./carteraView";
import CierreLlamada from "./cierreLlamada";
import { FRANJA_LABEL, Franja, hora12 } from "./formCita";
import { ahoraHM, marcador } from "./marcador";
import { errorTexto, nombreCompleto, statusClase } from "./salesHelpers";
import { useTelefono } from "./telefono/telefonoUI";
import "./marcadorDock.css";

/** El marcador para React (se vuelve a pintar cuando cambia). */
let version = 0;
marcador.suscribir(() => { version++; });
export const useMarcador = () => {
	useSyncExternalStore((cb) => marcador.suscribir(cb), () => version);
	return marcador;
};

const MOTIVOS_OCULTOS = ["heredado de Zoho", "sin status en Zoho"];
const RES_TEXTO: { [k: string]: string } = { contesto: "contestó", no_contesto: "no contestó", buzon: "buzón", ocupado: "ocupado", cortada: "se cortó", colgo: "me colgó", equivocado: "equivocado", saltado: "saltado" };

const reloj = (desde: number | null): string => {
	if (!desde) return "";
	const s = Math.max(0, Math.round((Date.now() - desde) / 1000));
	return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
};

interface Props {
	campusId: number;
	/** CRM ya los tiene; WappChat no: el marcador los pide (para el selector de cartera de supervisión y los motivos). */
	catalogs?: ProspectCatalogs | null;
	/** Abre a la persona en la vista de atrás (CRM: la ficha; WappChat: su conversación). */
	onAbrir: (id: number) => void;
	/** Herramientas → campañas de llamadas (las arma supervisión). */
	onCampanas?: () => void;
}

/**
 * Píldora "Marcador · 23" en la barra de CRM y WappChat, junto al teléfono, y el panel acoplado a la derecha
 * (Esteban, 2026-10-13: botón chico y sleek, visible a todas las asesoras). El panel no cambia la vista de atrás: la
 * ficha o el chat de la persona se abren donde ya estaba.
 */
const MarcadorDock: React.FC<Props> = ({ campusId, catalogs, onAbrir, onCampanas }) => {
	const m = useMarcador();
	useEffect(() => m.usar(campusId, onAbrir), [campusId, onAbrir]);
	useEffect(() => { if (catalogs) m.ponerCatalogos(catalogs); else m.cargarCatalogos(); }, [catalogs, campusId]);
	// La clase en <body> empuja la vista de atrás (y corre el teléfono y WappTeam) para que el panel no tape la ficha.
	useEffect(() => {
		document.body.classList.toggle("md-abierto", m.abierto);
		return () => document.body.classList.remove("md-abierto");
	}, [m.abierto]);
	// La píldora enseña lo de hoy (niveles 0–6), no la reserva de Interesados sin fecha y Potenciales (miles en SPS).
	const total = m.hoy;
	const urgente = m.urgente;
	return (
		<>
			<button type="button" className={"md-pildora" + (urgente ? " md-urgente" : "") + (m.abierto ? " on" : "") + (m.auto ? " auto" : "")}
				aria-expanded={m.abierto} aria-label={"Marcador" + (total !== null ? ", " + total + " por llamar hoy" : "")} title="Auto-marcador"
				onClick={() => m.ponerAbierto(!m.abierto)}>
				<WappIcono n="phone" size={16} />
				<span className="md-pildora-txt">Marcador</span>
				{total !== null ? <span className="md-pildora-n">{total.toLocaleString("es-MX")}</span> : null}
				{m.auto ? <span className="md-vivo" aria-hidden="true" /> : null}
			</button>
			{m.abierto ? createPortal(<Panel campusId={campusId} onCampanas={onCampanas} />, document.body) : null}
		</>
	);
};

export default MarcadorDock;

const Panel: React.FC<{ campusId: number; onCampanas?: () => void }> = ({ campusId, onCampanas }) => {
	const m = useMarcador();
	const tel = useTelefono();
	const [, tic] = useState(0);
	// El reloj de la llamada y los chips de las citas (disponible / pasó la hora) se mueven solos.
	useEffect(() => { const t = setInterval(() => tic((n) => n + 1), 1000); return () => clearInterval(t); }, []);
	// Debajo de la barra de CRM / WappChat (su alto cambia con la barra de arriba de WAPP).
	const [top, setTop] = useState<number>(110);
	useLayoutEffect(() => {
		const medir = () => { const b = document.querySelector(".crm-modulos")?.getBoundingClientRect().bottom; if (b) setTop(Math.max(0, Math.round(b))); };
		medir();
		window.addEventListener("resize", medir);
		return () => window.removeEventListener("resize", medir);
	}, []);
	const sup = !!m.catalogs?.isSupervisor;
	const cola = m.cola;
	const pend = m.pendientes;
	const ahora = ahoraHM();
	const a = m.actual;
	const enLlamada = !!a && tel.ocupado && tel.llamada?.prospectId === a.id;
	const llamadas = m.hechas.filter((h) => h.res !== "saltado").length;
	const contestaron = m.hechas.filter((h) => h.res === "contesto").length;
	const festejos = m.hechas.filter((h) => h.festejo).length;
	const nombreNivel = (n: number) => cola?.niveles.find((x) => x.nivel === n);

	return (
		<aside className="md-panel" aria-label="Auto-marcador" style={{ ["--md-top" as any]: top + "px" }}>
			<div className="md-cab">
				<div className="md-cab-tit">
					<b>Marcador</b>
					<span className="md-tenue">{cola ? (m.hoy ?? 0).toLocaleString("es-MX") + " hoy" + (cola.total > (m.hoy ?? 0) ? " · " + (cola.total - (m.hoy ?? 0)).toLocaleString("es-MX") + " en reserva" : "") : m.cargando ? "Cargando…" : ""}</span>
				</div>
				<button type="button" className="md-ico" onClick={() => m.cargar()} disabled={m.cargando} aria-label="Actualizar" title="Actualizar">
					<WappIcono n="restore" size={17} className={m.cargando ? "md-gira" : ""} /></button>
				<button type="button" className="md-ico" onClick={() => m.ponerAbierto(false)} aria-label="Cerrar el marcador" title="Minimizar (la sesión sigue)">
					<WappIcono n="x" size={18} /></button>
			</div>
			{sup && m.catalogs ? (
				<div className="md-cartera"><span className="md-tenue">Llamar como</span><CarteraSelect catalogs={m.catalogs} value={m.owner} onChange={(v) => m.ponerOwner(v)} /></div>
			) : null}
			{llamadas ? (
				<div className="md-marcas" aria-label="Tu sesión">
					<span><b>{llamadas}</b> {llamadas === 1 ? "llamada" : "llamadas"}</span><span><b>{contestaron}</b> {contestaron === 1 ? "contestó" : "contestaron"}</span>
					{festejos ? <span className="md-marca-oro"><b>{festejos}</b> {festejos === 1 ? "promesa" : "promesas"}</span> : null}
				</div>
			) : null}

			<div className="md-cuerpo">
				{cola?.citasHoy.length ? (
					<div className="md-citas">
						<span className="md-sec-tit">Citas de hoy</span>
						<div className="md-citas-tira">
							{cola.citasHoy.map((c) => {
								const disponible = !c.disponibleDesde || c.disponibleDesde <= ahora;
								const paso = !!c.hora && c.hora < ahora;
								const cuando = c.hora ? hora12(c.hora) : c.franja ? FRANJA_LABEL[c.franja as Franja] || c.franja : "Hoy";
								return (
									<button key={c.id} type="button" className={"md-cita" + (paso ? " paso" : disponible ? " lista" : "")} onClick={() => m.abrir(c.id)}
										title={(paso ? "Ya pasó la hora y nadie la cerró" : disponible ? "Ya se puede llamar" : "Entra a la lista a las " + hora12(c.disponibleDesde || "")) + (c.modalidad === "digital" ? " · digital" : "")}>
										<b>{cuando}</b> {c.nombre.split(" ")[0]}{c.intentos ? <small> · {c.intentos}×</small> : null}
									</button>
								);
							})}
						</div>
					</div>
				) : null}

				<div className="md-acciones">
					{!m.auto ? (
						<button type="button" className="md-btn pri md-empezar" disabled={!pend.length && !a} onClick={() => m.empezar()}>
							<WappIcono n="phone" size={16} />{m.hechas.length ? "Seguir" : "Empezar"}</button>
					) : (
						<button type="button" className="md-btn md-pausar" onClick={() => m.pausar()}><WappIcono n="pause" size={16} />Pausar</button>
					)}
					{a && !m.cierre ? <button type="button" className="md-btn" onClick={() => m.saltar()} disabled={enLlamada}>Saltar</button> : null}
					{a && !m.cierre && !enLlamada ? <button type="button" className="md-btn" onClick={() => m.registrar()} title="Marcaste desde tu celular: deja cómo terminó">Registrar</button> : null}
					<span className={"md-tel " + tel.estado} title={tel.error || ""}>{tel.estado === "listo" ? "Teléfono listo" : tel.estado === "error" ? "Teléfono sin conexión" : "Conectando…"}</span>
				</div>
				{m.aviso ? <div className="md-aviso">{m.aviso}</div> : null}
				{m.error ? <div className="md-aviso">{m.error}</div> : null}

				{a ? (
					<div className={"md-actual" + (enLlamada ? " sonando" : "")}>
						<div className="md-actual-cab">
							<span className="md-etq">{enLlamada ? (tel.llamada?.status === "en_llamada" ? "En llamada · " + reloj(tel.llamada.startedAt) : "Marcando…") : m.cierre ? "Terminó" : "Ahora"}</span>
							<span className="md-nivel">{a.nivelNombre}</span>
						</div>
						<button type="button" className="md-actual-nombre" onClick={() => m.abrir(a.id)}>{nombreCompleto(a)}</button>
						<div className="md-actual-sub">
							<span>{telefonoBonito(a.phone_e164)}</span>
							<span className={"chip " + statusClase(a.status_id)}>{a.status}</span>
						</div>
						<div className="md-motivo-txt">{a.motivo}{a.mejorHoraTexto ? " · " + a.mejorHoraTexto : ""}</div>
						{!m.cierre && !enLlamada ? (
							<div className="md-actual-acc">
								<button type="button" className="md-btn pri" disabled={tel.ocupado} onClick={() => m.llamar(a)}><WappIcono n="phone" size={15} />Llamar</button>
							</div>
						) : null}
						{m.cierre ? (
							<CierreLlamada key={a.id + "-" + (m.cierre.fin?.callId || "mano")} campusId={campusId} item={a} fin={m.cierre.fin} aviso={m.cierre.aviso} auto={m.auto}
								plantelId={campusId} motivosPotencial={(m.catalogs?.motivosPotencial || []).filter((x) => !MOTIVOS_OCULTOS.includes(x))}
								nota={m.notaDe(a.id)} onNota={(t) => m.ponerNota(a.id, t)} onGuardar={(b) => m.guardarCierre(b)} onCancelar={() => m.cancelarCierre()} />
						) : (
							<label className="md-nota">
								<span>Nota {enLlamada ? "(escribe mientras hablas)" : ""}</span>
								<textarea rows={2} value={m.notaDe(a.id)} onChange={(e) => m.ponerNota(a.id, e.target.value)} placeholder="Qué dijo, qué quedó" />
							</label>
						)}
					</div>
				) : m.espera ? <div className="md-espera">{m.espera}</div> : null}

				{m.nuevos ? (
					<button type="button" className="md-nuevos" onClick={() => { m.verNuevos(); document.querySelector(".md-lista")?.scrollIntoView({ behavior: "smooth", block: "start" }); }}>
						{m.nuevos === 1 ? "1 nuevo arriba · se va primero" : m.nuevos + " nuevos arriba · se van primero"}</button>
				) : null}

				<div className="md-lista">
					{pend.map((r, i) => {
						const nuevoNivel = i === 0 || pend[i - 1].nivel !== r.nivel;
						const nv = nuevoNivel ? nombreNivel(r.nivel) : null;
						return (
							<React.Fragment key={r.id}>
								{nuevoNivel ? <div className="md-sep"><span>{nv?.nombre || r.nivelNombre}</span><small>{(nv?.n ?? "").toLocaleString()}</small></div> : null}
								<Fila r={r} />
							</React.Fragment>
						);
					})}
					{cola && cola.queue.length < cola.total ? (
						<button type="button" className="md-mas" disabled={m.cargando} onClick={() => m.cargar(true)}>{m.cargando ? "Cargando…" : "Ver más"}</button>
					) : null}
					{cola && !pend.length && !m.cargando ? <div className="md-vacio">Nadie por llamar ahora.</div> : null}
				</div>

				<Agregar campusId={campusId} />

				{m.hechas.length ? (
					<details className="md-hechas">
						<summary>Hoy en esta pestaña · {m.hechas.length}</summary>
						{m.hechas.slice(0, 30).map((h, i) => (
							<button key={h.id + "-" + i} type="button" className="md-hecha" onClick={() => m.abrir(h.id)}>
								<span>{h.nombre}</span><small className={h.festejo ? "md-marca-oro" : ""}>{h.festejo ? "¡" + h.festejo + "!" : RES_TEXTO[h.res] || h.res}</small></button>
						))}
					</details>
				) : null}
				{onCampanas ? <button type="button" className="md-link md-campanas" onClick={onCampanas}>Campañas de llamadas →</button> : null}
			</div>
		</aside>
	);
};

/** Una persona de la lista: nombre (abre su perfil), curso, status, por qué está aquí; Llamar y Subir. */
const Fila: React.FC<{ r: DialerItem }> = ({ r }) => {
	const m = useMarcador();
	const [busy, setBusy] = useState(false);
	const curso = interesesCortos(r.course_interest_text).slice(0, 2).join(", ");
	const seguidas = r.call_noanswer_streak || 0;
	const noContesto = seguidas >= 2 ? "No contestó " + seguidas + "×" : r.last_call_result === "no_contesto" && r.dial_attempts_today > 0 ? "No contestó hoy" : null;
	const hacer = async (fn: () => Promise<void>) => { setBusy(true); try { await fn(); } catch (e) { m.ponerAviso(errorTexto(e)); } finally { setBusy(false); } };
	return (
		<div className={"md-fila" + (r.pin ? " fijada" : "") + (r.nivel <= 1 ? " arriba" : "")}>
			<div className="md-fila-cuerpo">
				<div className="l1">
					<button type="button" className="md-fila-nombre" onClick={() => m.abrir(r.id)}>{nombreCompleto(r)}</button>
					<span className={"chip " + statusClase(r.status_id)}>{r.status}</span>
				</div>
				<div className="l2">{[curso, r.motivo].filter(Boolean).join(" · ")}</div>
				{r.pin || r.mejorHoraTexto || noContesto ? (
					<div className="l3">
						{r.pin ? <span className="md-pin"><WappIcono n="pin" size={12} />por {r.pin.por.split(" ")[0]}{r.pin.nota ? ": " + r.pin.nota : ""}</span> : null}
						{r.mejorHoraTexto ? <span><WappIcono n="clock" size={12} /> {r.mejorHoraTexto}</span> : null}
						{noContesto ? <span className="md-oro">{noContesto}</span> : null}
					</div>
				) : null}
			</div>
			<div className="md-fila-acc">
				<button type="button" className="md-ico chico llamar" aria-label={"Llamar a " + nombreCompleto(r)} title="Llamar ahora" disabled={busy || !!m.cierre}
					onClick={() => m.llamar(r)}><WappIcono n="phone" size={15} /></button>
				{r.pin ? (
					<button type="button" className="md-ico chico" aria-label="Quitar de arriba" title="Quitar de arriba" disabled={busy} onClick={() => hacer(() => m.soltar(r.id))}><WappIcono n="x" size={15} /></button>
				) : (
					<button type="button" className="md-ico chico subir" aria-label={"Subir a " + nombreCompleto(r)} title="Subir: hasta arriba de la lista de hoy" disabled={busy}
						onClick={() => hacer(() => m.fijar(r.id))}><span aria-hidden="true">↑</span></button>
				)}
			</div>
		</div>
	);
};

/** "Agregar a alguien…": busca en el CRM (la asesora en su cartera; supervisión en todo lo que ve) y lo pone hasta arriba de la lista de hoy. */
const Agregar: React.FC<{ campusId: number }> = ({ campusId }) => {
	const m = useMarcador();
	const [abierto, setAbierto] = useState(false);
	const [q, setQ] = useState("");
	const [rows, setRows] = useState<ProspectRow[] | null>(null);
	const [hecho, setHecho] = useState<number[]>([]);
	useEffect(() => {
		if (!abierto || q.trim().length < 2) { setRows(null); return; }
		const t = setTimeout(() => {
			HttpManager.getInstance().prospectsService.list(campusId, { owner: m.catalogs?.isSupervisor ? "all" : "me", q: q.trim(), lite: 1, sinTotal: 1 } as any, 8, 1)
				.then((r) => setRows(r.data.data)).catch(() => setRows([]));
		}, 300);
		return () => clearTimeout(t);
	}, [q, abierto, campusId, m.catalogs]);
	if (!abierto) return <button type="button" className="md-agregar" onClick={() => setAbierto(true)}><WappIcono n="plus" size={15} />Agregar a alguien…</button>;
	return (
		<div className="md-buscar">
			<div className="md-buscar-cab">
				<input type="search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Nombre o teléfono" aria-label="Buscar a quién agregar" autoFocus />
				<button type="button" className="md-ico" onClick={() => { setAbierto(false); setQ(""); }} aria-label="Cerrar"><WappIcono n="x" size={16} /></button>
			</div>
			{rows?.map((r) => (
				<div key={r.id} className="md-buscar-fila">
					<span><b>{nombreCompleto(r)}</b><small>{[r.phone_e164 ? telefonoBonito(r.phone_e164) : null, r.status].filter(Boolean).join(" · ")}</small></span>
					<button type="button" className="md-btn mini" disabled={hecho.includes(r.id) || !r.phone_e164}
						onClick={() => m.fijar(r.id).then(() => setHecho((h) => [...h, r.id])).catch((e) => m.ponerAviso(errorTexto(e)))}>
						{hecho.includes(r.id) ? "Agregado" : "Agregar"}</button>
				</div>
			))}
			{rows && !rows.length ? <div className="md-vacio">Nadie con ese nombre o teléfono.</div> : null}
		</div>
	);
};
